const SHARPS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLATS = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];

// semitones: written = concert + semitones (includes octave transposition)
export const INSTRUMENT_GROUPS = [
  { key: 'C', label: 'Instruments in C', flats: false, items: [
    { id: 'guitar', name: 'Guitar', semitones: 0 },
    { id: 'violin', name: 'Violin', semitones: 0 },
    { id: 'viola', name: 'Viola', semitones: 0 },
    { id: 'cello', name: 'Cello', semitones: 0 },
    { id: 'flute', name: 'Flute', semitones: 0 },
    { id: 'piano', name: 'Piano', semitones: 0 },
    { id: 'bass-4', name: 'Bass (4 strings)', semitones: 0 },
    { id: 'bass-5', name: 'Bass (5 strings)', semitones: 0 },
  ] },
  { key: 'B♭', label: 'Instruments in B♭', flats: true, items: [
    { id: 'tenor-sax', name: 'Tenor Sax', semitones: 14 },
    { id: 'soprano-sax', name: 'Soprano Sax', semitones: 2 },
    { id: 'trumpet', name: 'Trumpet', semitones: 2 },
    { id: 'clarinet', name: 'Clarinet', semitones: 2 },
  ] },
  { key: 'E♭', label: 'Instruments in E♭', flats: true, items: [
    { id: 'alto-sax', name: 'Alto Sax', semitones: 9 },
    { id: 'baritone-sax', name: 'Baritone Sax', semitones: 21 },
  ] },
  { key: 'F', label: 'Instruments in F', flats: true, items: [
    { id: 'french-horn', name: 'French Horn', semitones: 7 },
  ] },
  { key: 'Perc', label: 'Percussion', flats: false, perc: true, items: [
    { id: 'timpani', name: 'Timpani', semitones: 0 },
    { id: 'drum-kit', name: 'Drum Kit', semitones: 0 },
    { id: 'hand-perc', name: 'Hand Percussion', semitones: 0 },
  ] },
];

const BASS4 = [
  { id: 'standard', name: 'Standard (E A D G)', notes: [28, 33, 38, 43] },
  { id: 'drop-d', name: 'Drop D (D A D G)', notes: [26, 33, 38, 43] },
  { id: 'half-down', name: 'Half Step Down (E♭ A♭ D♭ G♭)', notes: [27, 32, 37, 42], flats: true },
  { id: 'drop-c', name: 'Drop C (C G C F)', notes: [24, 31, 36, 41] },
];
const BASS5 = [
  { id: 'standard', name: 'Standard (B E A D G)', notes: [23, 28, 33, 38, 43] },
  { id: 'high-c', name: 'High C (E A D G C)', notes: [28, 33, 38, 43, 48] },
  { id: 'half-down', name: 'Half Step Down (B♭ E♭ A♭ D♭ G♭)', notes: [22, 27, 32, 37, 42], flats: true },
];

// Default string notes per count, used by the custom tuning editor
export const DEFAULT_STRINGS = {
  4: [28, 33, 38, 43],
  5: [23, 28, 33, 38, 43],
  6: [40, 45, 50, 55, 59, 64],
  7: [35, 40, 45, 50, 55, 59, 64],
  8: [30, 35, 40, 45, 50, 55, 59, 64],
};

const pad = (id, label, midi, lo = midi - 7, hi = midi + 7) => ({ id, label, midi, min: lo, max: hi });

// Percussion drums: default target note + adjustable range
export const PERCUSSION = {
  timpani: [
    pad('t32', '32"', 38, 38, 45), pad('t29', '29"', 41, 41, 48), pad('t26', '26"', 45, 45, 53),
    pad('t23', '23"', 50, 50, 57), pad('t20', '20"', 55, 53, 60),
  ],
  'drum-kit': [
    pad('kick', 'Kick', 33), pad('floor16', 'Floor Tom 16"', 43), pad('floor14', 'Floor Tom 14"', 48),
    pad('tom12', 'Tom 12"', 53), pad('tom10', 'Tom 10"', 57), pad('snare', 'Snare', 62),
  ],
  'hand-perc': [
    pad('djembe', 'Djembe', 64), pad('tumba', 'Tumba', 53), pad('conga', 'Conga', 57), pad('quinto', 'Quinto', 62),
    pad('bongo-h', 'Bongo Hembra', 67), pad('bongo-m', 'Bongo Macho', 72), pad('dayan', 'Tabla Dayan', 61), pad('bayan', 'Tabla Bayan', 49),
  ],
};

export const GUITAR_TUNINGS = [
  { id: 'standard', name: 'Standard (E A D G B E)', notes: [40, 45, 50, 55, 59, 64] },
  { id: 'drop-d', name: 'Drop D (D A D G B E)', notes: [38, 45, 50, 55, 59, 64] },
  { id: 'dadgad', name: 'DADGAD (D A D G A D)', notes: [38, 45, 50, 55, 57, 62] },
  { id: 'open-g', name: 'Open G (D G D G B D)', notes: [38, 43, 50, 55, 59, 62] },
  { id: 'open-d', name: 'Open D (D A D F# A D)', notes: [38, 45, 50, 54, 57, 62] },
  { id: 'open-e', name: 'Open E (E B E G# B E)', notes: [40, 47, 52, 56, 59, 64] },
  { id: 'half-down', name: 'Half Step Down (E♭ A♭ D♭ G♭ B♭ E♭)', notes: [39, 44, 49, 54, 58, 63], flats: true },
  { id: 'drop-c', name: 'Drop C (C G C F A D)', notes: [36, 43, 48, 53, 57, 62] },
];

// Instruments with selectable/custom tunings
export const TUNABLE = {
  guitar: { presets: GUITAR_TUNINGS, counts: [6, 7, 8] },
  'bass-4': { presets: BASS4, counts: [4] },
  'bass-5': { presets: BASS5, counts: [5] },
};

// Standard tuning, concert MIDI numbers (low to high)
export const STRINGS = {
  guitar: [40, 45, 50, 55, 59, 64],
  violin: [55, 62, 69, 76],
  viola: [48, 55, 62, 69],
  cello: [36, 43, 50, 57],
};

export const noteName = (midi, flats) => `${(flats ? FLATS : SHARPS)[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;
export const pitchClass = (pc, flats) => (flats ? FLATS : SHARPS)[pc];
export const midiToFreq = (midi, a4) => a4 * 2 ** ((midi - 69) / 12);
export const freqToMidiFloat = (f, a4) => 69 + 12 * Math.log2(f / a4);

export const INTERVALS = ['Unison', 'Minor 2nd', 'Major 2nd', 'Minor 3rd', 'Major 3rd', 'Perfect 4th', 'Tritone', 'Perfect 5th', 'Minor 6th', 'Major 6th', 'Minor 7th', 'Major 7th'];
export const INTERVAL_SHORT = ['P1', 'm2', 'M2', 'm3', 'M3', 'P4', 'TT', 'P5', 'm6', 'M6', 'm7', 'M7'];

// 5-limit just intonation, cents above the root for each semitone
const JUST_CENTS = [0, 111.73, 203.91, 315.64, 386.31, 498.04, 590.22, 701.96, 813.69, 884.36, 1017.6, 1088.27];

// Interval of a detected pitch above/below a drone (both concert MIDI)
export function describeInterval(midiFloat, droneMidi, just = false) {
  const diff = midiFloat - droneMidi;
  const semis = Math.round(diff);
  const abs = Math.abs(semis);
  const pc = abs % 12;
  const octaves = Math.floor(abs / 12);
  const target = Math.sign(semis) * (octaves * 1200 + (just ? JUST_CENTS[pc] : pc * 100));
  const cents = Math.round(diff * 100 - target);
  const isOctave = pc === 0 && semis !== 0;
  const name = isOctave ? (octaves > 1 ? `${octaves} Octaves` : 'Octave') : INTERVALS[pc];
  return {
    short: isOctave ? `P8${octaves > 1 ? `×${octaves}` : ''}` : INTERVAL_SHORT[pc],
    name: `${name}${!isOctave && octaves ? ` +${octaves} oct` : ''}${semis < 0 ? ' below' : ''}`,
    cents,
    semis,
    degree: ((semis % 12) + 12) % 12,
  };
}

export const findInstrument = (id) => {
  for (const g of INSTRUMENT_GROUPS) {
    const item = g.items.find((i) => i.id === id);
    if (item) return { ...item, group: g };
  }
  return { ...INSTRUMENT_GROUPS[0].items[0], group: INSTRUMENT_GROUPS[0] };
};

export function describePitch(freq, a4, semitones, flats) {
  const midiFloat = 69 + 12 * Math.log2(freq / a4);
  const midi = Math.round(midiFloat);
  const cents = Math.round((midiFloat - midi) * 100);
  const written = midi + semitones;
  const names = flats ? FLATS : SHARPS;
  return {
    note: names[((written % 12) + 12) % 12],
    pc: ((written % 12) + 12) % 12,
    octave: Math.floor(written / 12) - 1,
    concert: names[((midi % 12) + 12) % 12],
    concertOctave: Math.floor(midi / 12) - 1,
    cents,
  };
}

// Autocorrelation pitch detection (ACF with parabolic interpolation)
export function autoCorrelate(buf, sampleRate) {
  const size = buf.length;
  let rms = 0;
  for (let i = 0; i < size; i++) rms += buf[i] * buf[i];
  if (Math.sqrt(rms / size) < 0.01) return -1;

  let r1 = 0;
  let r2 = size - 1;
  const thres = 0.2;
  for (let i = 0; i < size / 2; i++) if (Math.abs(buf[i]) < thres) { r1 = i; break; }
  for (let i = 1; i < size / 2; i++) if (Math.abs(buf[size - i]) < thres) { r2 = size - i; break; }
  const b = buf.slice(r1, r2);
  const n = b.length;

  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0;
    for (let j = 0; j < n - i; j++) s += b[j] * b[j + i];
    c[i] = s;
  }
  let d = 0;
  while (d < n - 1 && c[d] > c[d + 1]) d++;
  let maxVal = -1;
  let maxPos = -1;
  for (let i = d; i < n; i++) if (c[i] > maxVal) { maxVal = c[i]; maxPos = i; }
  if (maxPos <= 0) return -1;

  let t0 = maxPos;
  const x1 = c[t0 - 1];
  const x2 = c[t0];
  const x3 = c[t0 + 1] ?? x2;
  const a = (x1 + x3 - 2 * x2) / 2;
  const bb = (x3 - x1) / 2;
  if (a) t0 -= bb / (2 * a);
  return sampleRate / t0;
}
